"use client";
import { jsx, jsxs } from 'react/jsx-runtime';
import { faAngleDown } from '@optimizely/pro-light-svg-icons';
import classNames from 'classnames';
import omit from 'lodash/omit';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { Manager, Reference, Popper } from 'react-popper';
import { useClickAway } from '../../hooks/useClickAway.js';
import Button from '../Button/index.js';
import { Icon } from '../Icon/icon.js';
import { DropdownBlockLink } from './DropdownBlockLink/index.js';
import DropdownBlockLinkSecondaryText from './DropdownBlockLinkSecondaryText/index.js';
import DropdownBlockLinkText from './DropdownBlockLinkText/index.js';
import { DropdownContents } from './DropdownContents/index.js';
import DropdownListItem from './DropdownListItem/index.js';
import DropdownRadio from './DropdownRadio/index.js';

var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
function ClickAway({ children, onClickAway }) {
  const ref = useRef();
  useClickAway(ref, onClickAway);
  return React.cloneElement(children, { ref });
}
class Dropdown extends React.PureComponent {
  constructor(props) {
    super(props);
    __publicField(this, "componentDidUpdate", (prevProps) => {
      if (this.shouldDisplayChildren(this.state.isOpen, prevProps.isDisabled) === this.shouldDisplayChildren(this.state.isOpen, this.props.isDisabled)) {
        return;
      }
      if (this.shouldDisplayChildren(this.state.isOpen, this.props.isDisabled)) {
        document.addEventListener("click", this.handleOnBlur, { capture: true });
      } else {
        document.removeEventListener("click", this.handleOnBlur, {
          capture: true
        });
      }
    });
    __publicField(this, "handleHideChildren", () => {
      const { shouldHideChildrenOnClick } = this.props;
      if (!shouldHideChildrenOnClick) {
        return;
      }
      this.hide();
    });
    __publicField(this, "handleOnBlur", () => {
      if (this.props.shouldHideChildrenOnTriggerBlur) {
        this.hide();
      }
    });
    __publicField(this, "handleToggle", (event) => {
      const { isDisabled } = this.props;
      if (isDisabled || event.ignoreToggle) {
        return;
      }
      this.toggle();
    });
    __publicField(this, "hide", () => this.setState({ isOpen: false }));
    __publicField(this, "shouldDisplayChildren", (isOpen, isDisabled) => isOpen && !isDisabled);
    __publicField(this, "show", () => this.setState({ isOpen: true }));
    __publicField(this, "toggle", () => {
      this.setState(({ isOpen }) => {
        return { isOpen: !isOpen };
      });
    });
    this.state = {
      isOpen: props.isOpen
    };
  }
  render() {
    const {
      arrowIcon,
      buttonContent,
      buttonTitle,
      children,
      className,
      displayError,
      fullWidth,
      icon,
      isDisabled,
      isLabelTight,
      placement,
      renderActivator,
      style,
      testSection,
      whiteBackground,
      width,
      zIndex,
      ...props
    } = this.props;
    const { isOpen } = this.state;
    const groupClass = classNames(
      "oui-dropdown-group",
      { ["width--1-1"]: fullWidth },
      { ["oui-form-bad-news"]: displayError },
      { ["is-active"]: isOpen },
      className
    );
    const buttonClass = classNames("oui-button oui-dropdown-trigger", {
      [`oui-button--${style}`]: style,
      ["soft--left text--left"]: fullWidth,
      ["soft--sides"]: isLabelTight
    });
    const safeProps = omit(props, [
      "isOpen",
      "shouldHideChildrenOnClick",
      "shouldHideChildrenOnTriggerBlur"
    ]);
    const element = /* @__PURE__ */ jsx(
      "div",
      {
        className: groupClass,
        "data-oui-component": true,
        "data-test-section": testSection,
        ...safeProps,
        children: /* @__PURE__ */ jsxs(Manager, { children: [
          /* @__PURE__ */ jsx(Reference, { children: ({ ref }) => {
            if (buttonContent) {
              return /* @__PURE__ */ jsx(
                Button,
                {
                  "aria-haspopup": "listbox",
                  buttonRef: ref,
                  className: buttonClass,
                  disabled: isDisabled,
                  onClick: this.handleToggle,
                  style,
                  whiteBackground,
                  width: fullWidth ? "full" : "default",
                  children: /* @__PURE__ */ jsxs("div", { className: "flex flex-align--center", children: [
                    /* @__PURE__ */ jsx("div", { className: "flex--1 truncate", children: buttonContent.label ? /* @__PURE__ */ jsxs("div", { className: "line--tight text--left push--right", children: [
                      /* @__PURE__ */ jsxs("div", { className: "micro muted", children: [
                        buttonContent.label,
                        /* @__PURE__ */ jsx("span", { className: "oui-assistive-text", children: ":" })
                      ] }),
                      /* @__PURE__ */ jsx("div", { children: buttonContent.content })
                    ] }) : buttonContent }),
                    !!arrowIcon && arrowIcon !== "none" && /* @__PURE__ */ jsx(Icon, { icon: faAngleDown, size: "small" })
                  ] })
                }
              );
            }
            if (renderActivator) {
              return renderActivator({
                buttonRef: ref,
                buttonTitle,
                disabled: isDisabled,
                hideDropdown: this.hide,
                isDropdownOpen: isOpen,
                onClick: this.handleToggle,
                ref,
                showDropdown: this.show,
                testSection
              });
            } else if (this.props.activator) {
              return React.cloneElement(this.props.activator, {
                buttonRef: ref,
                onClick: this.handleToggle,
                ref
              });
            }
          } }),
          this.shouldDisplayChildren(isOpen, isDisabled) && /* @__PURE__ */ jsx(Popper, { placement, children: ({ placement: popperPlacement, ref, style: popperStyle }) => /* @__PURE__ */ jsx(
            "div",
            {
              className: "oui-dropdown-children",
              "data-placement": popperPlacement,
              onClick: (event) => !event.ignoreToggle && this.handleHideChildren(),
              ref,
              style: {
                borderRadius: "var(--border-radius)",
                boxShadow: "0 2px 3px rgba(0,0,0,.1)",
                marginBottom: 2,
                marginTop: 2,
                position: "absolute",
                width: fullWidth ? "100%" : width,
                zIndex,
                ...popperStyle
              },
              children: typeof children === "function" ? children({ handleHideChildren: this.hide }) : children
            }
          ) })
        ] })
      }
    );
    return /* @__PURE__ */ jsx(ClickAway, { onClickAway: this.handleOnBlur, children: element });
  }
}
__publicField(Dropdown, "displayName", "Dropdown");
Dropdown.propTypes = {
  /**
   * React element that when clicked activates the dropdown
   * Either this prop OR buttonContent should be used, not both
   * Not included in defaultProps because undefined is an expected value
   */
  activator: PropTypes.elementType,
  /** Arrow icon direction:
   * - Defaults to 'none', which hides the arrow
   * - passing a prop value of false also hides the arrow
   * - passing a prop value of true uses down arrow
   */
  arrowIcon: PropTypes.oneOf(["down", "none"]),
  /**
   * Button text, can be a string or element.
   * Either this prop OR activator should be used, not both
   * Not included in defaultProps because undefined is an expected value
   */
  buttonContent: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.shape({ content: PropTypes.string, label: PropTypes.string })
  ]),
  /** Disable button. */
  buttonTitle: PropTypes.string,
  /**
   * Dropdown contents, typically using the <Blocklist> component
   * Use render prop function for custom hide configuration
   */
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
  /** CSS class names. */
  className: PropTypes.string,
  /** Show error state. */
  displayError: PropTypes.bool,
  /** Button width is either full or inline-block. */
  fullWidth: PropTypes.bool,
  /** Disable button. */
  isDisabled: PropTypes.bool,
  /** Whether button width is narrow or full. Defaults to false. */
  isLabelTight: PropTypes.bool,
  /**  Dropdown can be open at start if true. Defaults to false */
  isOpen: PropTypes.bool,
  /** Popper placement property */
  placement: PropTypes.oneOf([
    "top",
    "top-start",
    "top-end",
    "bottom",
    "bottom-start",
    "bottom-end",
    "right",
    "right-start",
    "right-end",
    "left",
    "left-start",
    "left-end"
  ]),
  /**
   * A render prop to render the activator button that when clicked activates
   * the dropdown
   * Either this prop OR buttonContent should be used, not both
   */
  renderActivator: PropTypes.func,
  /**
   * Whether Dropdown children should be hidden on children click
   * - Defaults to true
   * - Pass render prop function as child prop and use provided
   *   handleHideChildren function to programmatically hide
   */
  shouldHideChildrenOnClick: PropTypes.bool,
  /**
   * Whether Dropdown children should be hidden when trigger element blurs.
   * This is intended to enable auto-focusing elements inside the popup
   * - Defaults to true
   */
  shouldHideChildrenOnTriggerBlur: PropTypes.bool,
  /**
   * Button style, e.g. highlight, danger, outline
   * Not included in defaultProps because undefined is an expected value
   */
  style: PropTypes.string,
  /** For automated testing only. */
  testSection: PropTypes.string,
  /** Enable white background for Outline styled button */
  whiteBackground: PropTypes.bool,
  /** Dropdown menu width, in pixels. */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Override default dropdown menu z-index. */
  zIndex: PropTypes.number
};
Dropdown.defaultProps = {
  arrowIcon: "none",
  displayError: false,
  fullWidth: false,
  isDisabled: false,
  isLabelTight: false,
  isOpen: false,
  placement: "bottom-start",
  renderActivator: null,
  shouldHideChildrenOnClick: true,
  shouldHideChildrenOnTriggerBlur: true,
  testSection: "",
  width: 200,
  zIndex: 999
};
Dropdown.Contents = DropdownContents;
Dropdown.ListItem = DropdownListItem;
Dropdown.BlockLink = DropdownBlockLink;
Dropdown.BlockLinkText = DropdownBlockLinkText;
Dropdown.BlockLinkSecondaryText = DropdownBlockLinkSecondaryText;
Dropdown.Radio = DropdownRadio;

export { Dropdown as default };
